import { useMemo } from 'react';
import tinycolor from 'tinycolor2';
import { formatColor, type GradientColorResult } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { getUtf8ByteLength } from '@meitu/saas-editor-core/es/utils/text-kit/utf8';
import { type ITextLayerProps, type ITexture } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { TextureOptions } from '@meitu/xdesign-widgets/dist/colorGroup';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useTextRange, useTextRangeAttrsState } from '@/hooks/use-text-range-attrs-state';
import { checkIsInArtTextGeneratorMode } from '@/utils/mode';
import { useLayerProps } from '../../hooks';
import { type TexturePanelRenderFn, type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type Props = WidgetCommonProps & {
    useForFill?: boolean;
    size?: { width: number; height: number };
    panelPosition?: string;
    renderTexturePanel?: TexturePanelRenderFn;
};

const FALLBACK_COLOR = '#000000ff';

const formatTextGradientColor = (textGradient: GradientColorResult) => {
    const gradient = {
        ...textGradient,
        colors: textGradient.colors.map((item) => {
            return {
                color: tinycolor(item.color).toHex8String(),
                value: item.value,
            };
        }),
    };
    return formatColor(gradient) as string;
};

export const TextColorPicker: React.FC<Props> = ({
    useForFill = false,
    size,
    renderTexturePanel,
    panelPosition,
    layerId,
    recordId,
}) => {
    const editor = useEditor();
    const textRangeAttrsState = useTextRangeAttrsState(layerId);
    const textRange = useTextRange(layerId);
    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { color, textGradient, texture, shapeExtension } = layerData;

        return {
            color,
            textGradient,
            texture,
            shapeExtension,
        };
    });
    const popoverWidgetRef = useGlobalWidget(editor, false);

    const color = props ? props.color : FALLBACK_COLOR;
    const textGradient = props ? props.textGradient : null;
    const texture = props ? props.texture : undefined;
    const shapeExtension = props ? props.shapeExtension : null;
    const isInArtTextGeneratorMode = checkIsInArtTextGeneratorMode();

    const selectionTextAttrs =
        textRangeAttrsState?.type === 'uniform' || textRangeAttrsState?.type === 'mixed'
            ? textRangeAttrsState.attrs
            : null;
    const hasSelectionTextAttrs = Boolean(selectionTextAttrs);
    const selectionColor = selectionTextAttrs?.color;

    const session = editor.getInstance<TextEditorPlugin>('TextEditorPlugin')?.getSession();
    const hasTextSelection = !!textRange && textRange.start !== textRange.end;
    // 选区使用 UTF-8 字节偏移；全文长度取编辑会话，避免输入过程中持久化文案滞后。
    const isFullTextSelection =
        hasTextSelection &&
        session?.editingLayer.id === layerId &&
        textRange.start === 0 &&
        textRange.end === getUtf8ByteLength(session.adapter.getText(session.editingLayer));
    const hasPartialTextSelection = hasTextSelection && !isFullTextSelection;

    const finalColor = useMemo(() => {
        if ((!hasSelectionTextAttrs || isFullTextSelection) && textGradient && textGradient.colors.length > 0) {
            return formatTextGradientColor(textGradient);
        }
        if (selectionColor) return selectionColor;
        return color;
    }, [color, textGradient, selectionColor, hasSelectionTextAttrs, isFullTextSelection]);

    const disableGradient = hasPartialTextSelection || !!shapeExtension;
    const disableTexture = hasPartialTextSelection || !!shapeExtension;

    // ATTENTION: ColorGroup 设计有问题（textureData必须要有值），textureData 先按照旧项目中定义的方式传入
    const textureData = useMemo<ITexture | undefined>(() => {
        if (disableTexture) return undefined;

        return {
            name: texture?.name,
            url: texture?.url ?? '',
            stretch: texture?.stretch ?? TextureOptions()?.[0].value,
            scale: texture?.scale ?? 1,
        };
    }, [texture, disableTexture]);

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openTextColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                isFill: useForFill,
                ...(panelPosition ? { panelPosition } : {}),
            });
        }
    };

    const setFill = (color: string, writeHistory: boolean) => {
        const textLayer = editor.getInstance<TextLayer>('TextLayer');
        const options = { writeHistory };

        return color.startsWith('linear-gradient')
            ? textLayer.setFillGradient(layerId, color, options)
            : textLayer.setFillColor(layerId, color, options);
    };

    const handleChangeColor = (color: string) => {
        if (isInArtTextGeneratorMode) {
            return editor.getInstance<TextLayer>('TextLayer').adjustArtTextSingleCharacter(layerId, { color });
        }

        setFill(color, false);
    };

    const handleAfterChangeColor = (color: string) => {
        if (isInArtTextGeneratorMode) {
            editor
                .getInstance<TextLayer>('TextLayer')
                .adjustArtTextSingleCharacter(layerId, { color }, { writeHistory: true });
            return;
        }

        setFill(color, true);

        Tracker.setTextColor({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            color,
            isFill: useForFill,
            ...(panelPosition ? { panelPosition } : {}),
        });
    };

    const handleChangeTexture = (texture: ITexture) => {
        if (!texture.url) return;

        editor.getInstance<TextLayer>('TextLayer').setFillTexture(layerId, texture);

        Tracker.setTexture({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            texture,
            ...(panelPosition ? { panelPosition } : {}),
        });
    };

    return (
        <ColorGroup
            isGradient={!disableGradient}
            isTexture={!disableTexture}
            textureData={textureData}
            placement="left"
            showIcon={!useForFill}
            popupTriggerSize={size || { width: 92, height: 34 }}
            color={finalColor}
            onOpenChange={handleColorPickerOpenChange}
            setColor={handleChangeColor}
            setColorAfter={(color, _, { isBrandColor }) => {
                handleAfterChangeColor(color);
                if (isBrandColor) {
                    window._stat_track?.('hb_layers_func_use', {
                        func_id: '1_107',
                    });
                }
            }}
            textureChange={handleChangeTexture}
            onToggleBizTab={(bizTab) => {
                if (bizTab === 'team') {
                    window._stat_track?.('hb_layers_func_click', {
                        func_id: '1_107',
                    });
                }
            }}
            popupOverlayClassName="text-panel-color-picker"
            selectTextureImageModal={renderTexturePanel}
        />
    );
};
